import React, { useContext, useState } from 'react';
import { FBAInboundContext } from '../FBAInboundContext';
import { WAREHOUSE_ADDRESSES } from '../services/fbaInboundApi';
import { MapPin, Building, CheckCircle, Loader2 } from 'lucide-react';

const Step1_AddressAssignment = () => {
  const {
    outboundShipment,
    clients,
    setShipFromAddress,
    nextStep,
    loading
  } = useContext(FBAInboundContext);

  const [selectedCountry, setSelectedCountry] = useState('CA');
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Get client name from outbound
  const clientId = outboundShipment?.team_id;
  const client = clients.find(c => c.id === clientId);
  const clientName = client?.name || '';

  // Check for special clients (Abdul/Anum Basit)
  const isSpecialClient = clientName.toLowerCase().includes('abdul') ||
                          clientName.toLowerCase().includes('anum') ||
                          clientName.toLowerCase().includes('basit');

  // Get available addresses based on country
  const getAddressOptions = () => {
    const options = [];

    if (isSpecialClient) {
      options.push({
        id: WAREHOUSE_ADDRESSES.ABDUL_ANUM,
        label: 'Abdul/Anum Basit Warehouse',
        description: 'Special warehouse for Abdul/Anum clients',
        country: 'US'
      });
    }

    if (selectedCountry === 'CA') {
      options.push({
        id: WAREHOUSE_ADDRESSES.CA,
        label: 'Canada Warehouse',
        description: 'YYZ Prep Canada facility',
        country: 'CA'
      });
    } else {
      options.push({
        id: WAREHOUSE_ADDRESSES.US,
        label: 'US Warehouse',
        description: 'YYZ Prep US facility',
        country: 'US'
      });
    }

    return options;
  };

  const addressOptions = getAddressOptions();

  const handleSubmit = async () => {
    if (!selectedAddressId) return;

    setIsSubmitting(true);
    try {
      await setShipFromAddress(selectedAddressId);
      setSuccess(true);
      setTimeout(() => {
        nextStep();
      }, 1000);
    } catch (err) {
      console.error('Failed to set address:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Address Assigned Successfully
        </h3>
        <p className="text-gray-600">
          Proceeding to next step...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Shipment Info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-start space-x-4">
          <div className="p-2 bg-white rounded-lg border border-gray-200">
            <Building className="w-6 h-6 text-gray-600" />
          </div>
          <div>
            <div className="text-sm font-medium text-gray-500">Outbound Shipment</div>
            <div className="text-lg font-semibold text-gray-900">
              #{outboundShipment?.id} - {outboundShipment?.name || 'Unnamed'}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              Client: {clientName || 'Unknown'}
            </div>
          </div>
        </div>
      </div>

      {/* Country Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Destination Country
        </label>
        <div className="grid grid-cols-2 gap-3">
          {['CA', 'US'].map((country) => (
            <button
              key={country}
              onClick={() => {
                setSelectedCountry(country);
                setSelectedAddressId(null);
              }}
              className={`p-4 border-2 rounded-lg text-center transition-all ${
                selectedCountry === country
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-2xl mb-1">{country === 'CA' ? '🇨🇦' : '🇺🇸'}</div>
              <div className="font-medium text-gray-900">
                {country === 'CA' ? 'Canada' : 'United States'}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Special Client Notice */}
      {isSpecialClient && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center text-amber-800">
            <Building className="w-5 h-5 mr-2" />
            <span className="font-medium">Special Client Detected</span>
          </div>
          <p className="text-sm text-amber-700 mt-1">
            This client uses a special warehouse address. This has been pre-selected for you.
          </p>
        </div>
      )}

      {/* Address Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Warehouse Address
        </label>
        <div className="space-y-3">
          {addressOptions.map((address) => (
            <button
              key={address.id}
              onClick={() => setSelectedAddressId(address.id)}
              className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                selectedAddressId === address.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <MapPin className={`w-5 h-5 ${
                    selectedAddressId === address.id ? 'text-blue-500' : 'text-gray-400'
                  }`} />
                  <div>
                    <div className="font-medium text-gray-900">{address.label}</div>
                    <div className="text-sm text-gray-500">{address.description}</div>
                  </div>
                </div>
                {selectedAddressId === address.id && (
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Submit Button */}
      <div className="pt-4">
        <button
          onClick={handleSubmit}
          disabled={!selectedAddressId || isSubmitting || loading}
          className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Setting Address...
            </>
          ) : (
            <>
              <MapPin className="w-5 h-5 mr-2" />
              Set Ship-From Address
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Step1_AddressAssignment;
